import { useState } from 'react';
import classNames from 'classnames';

import { IconButton } from '@/components/button';
import { Icon } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';
import { WaveLoader } from '@/components/wave-loader';
import { ConversationJob } from '@/explore/types';

import {
  buildNestedJobs,
  getJobTreeStatus,
  getJobTreeStatusMessage,
  NestedConversationJob,
} from '@/core/conversation/job';

import { JobNode } from './job-node';
import { StatusMessageContent } from './status-message-content';

import styles from './job-tree.module.scss';

export type { NestedConversationJob };

interface JobTreeProps {
  jobs: ConversationJob[];
  onRetry: () => void;
  onAbort: (jobId: string) => void;
}

export const JobTree = ({ jobs, onRetry, onAbort }: JobTreeProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const nestedJobs = buildNestedJobs(jobs);
  const status = getJobTreeStatus(nestedJobs);
  const latestProgress = getJobTreeStatusMessage(nestedJobs);

  return (
    <div className={classNames(styles.jobTree, { [styles.completed]: status === 'completed' })}>
      <div className={styles.collapsedJobTree} onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className={styles.collapsedContent}>
          {status === 'running' ? (
            <WaveLoader className={styles.loader} />
          ) : status === 'error' ? (
            <Icon name="X" size={16} className={styles.errorIcon} />
          ) : (
            <Icon name="Check" size={16} className={styles.completedIcon} />
          )}
          <span className={styles.collapsedMessage}>
            <StatusMessageContent
              content={
                status === 'completed'
                  ? 'Completed'
                  : (latestProgress?.statusMessage ?? 'Processing...')
              }
              uniqueId={latestProgress?.jobProgressId ?? 'processing'}
            />
          </span>
        </div>
        <div className={styles.overviewActions}>
          {status === 'running' ? (
            <Tooltip content="Cancel the request">
              <button
                type="button"
                className={styles.abortBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  const rootJob = nestedJobs.find((job) => job.parentJobId === undefined);
                  if (rootJob !== undefined) {
                    onAbort(rootJob.jobId);
                  }
                }}
              />
            </Tooltip>
          ) : status === 'error' ? (
            <Tooltip content="Try again">
              <IconButton
                icon="RotateCcw"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry();
                }}
              />
            </Tooltip>
          ) : null}
          <Tooltip content={isCollapsed ? 'Expand job details' : 'Collapse job details'}>
            <IconButton
              icon={isCollapsed ? 'ChevronDown' : 'ChevronUp'}
              size="small"
              className={styles.collapseToggle}
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(!isCollapsed);
              }}
            />
          </Tooltip>
        </div>
      </div>
      <div className={classNames(styles.jobNodes, { [styles.hidden]: isCollapsed })}>
        {nestedJobs.map((job) => (
          <JobNode key={job.jobId} job={job} depth={0} onToggle={() => setIsCollapsed(true)} />
        ))}
      </div>
    </div>
  );
};
